import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { notificationsApi } from '@/api/notifications';
import { progressApi } from '@/api/progress';
import { usersApi } from '@/api/users';
import { useAuthStore } from '@/stores/auth';
import { StudentTopBar } from '@/components/layout/StudentTopBar';
import { LearnSidebar } from '../desktop/LearnSidebar';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MSegmented, MTitle } from '@/components/mobile/MobileKit';
import { StudentLessonsListPage } from '../StudentLessonsListPage';
import { StudentHomeworkPage } from '../StudentHomeworkPage';
import { StudentLeaderboardPage } from '../StudentLeaderboardPage';

/**
 * «Обучение» — the single learning section that gathers Уроки (lessons),
 * Домашка (homework) and Рейтинг (leaderboard) behind tabs, replacing three
 * separate nav items. Each tab renders its existing page in `embedded` mode
 * (its own top title suppressed, since the hub provides one). The active tab
 * seeds from `?tab=` for deep links, then lives in local state so an embedded
 * page touching the URL can't knock the hub back to the first tab.
 */
const TABS = [
  { key: 'lessons', labelKey: 'learningHub.tabLessons', icon: 'menu_book' },
  { key: 'homework', labelKey: 'learningHub.tabHomework', icon: 'assignment' },
  { key: 'rating', labelKey: 'learningHub.tabRating', icon: 'leaderboard' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export function StudentLearningHubPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const [active, setActive] = useState<TabKey>(() => {
    const r = params.get('tab');
    return TABS.some((x) => x.key === r) ? (r as TabKey) : 'lessons';
  });
  const setTab = (k: TabKey) => {
    setActive(k);
    setParams(k === 'lessons' ? {} : { tab: k }, { replace: true });
  };

  // Для шапки и правой колонки. Ключи те же, что на главной, — запросы
  // берутся из общего хранилища и второй раз не уходят.
  const user = useAuthStore((s) => s.user);
  const stats = useQuery({ queryKey: ['student', 'stats'], queryFn: progressApi.getMyStats });
  const unread = useQuery({ queryKey: ['student', 'unread'], queryFn: notificationsApi.unreadCount });
  const marathons = useQuery({
    queryKey: ['student', 'enrolled-marathons'],
    queryFn: () => usersApi.myEnrolledMarathons(),
  });
  const marathonIds = (marathons.data ?? []).map((m) => m.id);

  // Телефон: заголовок компактнее, вкладки — таблеткой из макета (23.08.2026),
  // без иконок: три подписи по-казахски и так занимают всю ширину.
  if (isMobile) {
    return (
      <div className="space-y-3.5">
        <MTitle title={t('learningHub.title')} subtitle={t('learningHub.subtitle')} />
        <MSegmented
          tabs={TABS.map((tab) => ({ key: tab.key, label: t(tab.labelKey) }))}
          active={active}
          onChange={setTab}
        />
        {active === 'lessons' && <StudentLessonsListPage embedded />}
        {active === 'homework' && <StudentHomeworkPage embedded />}
        {active === 'rating' && <StudentLeaderboardPage embedded />}
      </div>
    );
  }

  return (
    <div>
      <StudentTopBar
        title={t('learningHub.title')}
        subtitle={t('learningHub.subtitle')}
        streak={stats.data?.current_streak ?? 0}
        unread={unread.data?.count ?? 0}
        name={user?.full_name}
        avatarUrl={user?.avatar_url}
      />

      {/* Переключатель вкладок таблеткой из макета: подпись без значка,
          выбранная — сплошным оранжевым. */}
      <div className="mb-5 flex w-max gap-1 rounded-full bg-surface-container p-1">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setTab(tab.key)}
            className={cn(
              'rounded-full px-5 py-[9px] text-[12.5px] font-semibold transition',
              active === tab.key
                ? 'bg-primary text-on-primary'
                : 'text-on-surface-variant hover:bg-surface-container-high',
            )}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {/* Две колонки: слева вкладка, справа — куратор, навыки, занятие,
          материалы. Правая колонка ОБЩАЯ для всех трёх вкладок, как в макете. */}
      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div>
          {active === 'lessons' && <StudentLessonsListPage embedded />}
          {active === 'homework' && <StudentHomeworkPage embedded />}
          {active === 'rating' && <StudentLeaderboardPage embedded />}
        </div>
        <LearnSidebar marathonIds={marathonIds} />
      </div>
    </div>
  );
}
